import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Lightning, Drop, Fire, Plus, Trash, ChartBar } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const TYPE_CONFIG = {
  electricity: { icon: Lightning, color: '#F59E0B', label: 'Electricity', unit: 'kWh' },
  gas: { icon: Fire, color: '#FF6B35', label: 'Gas', unit: 'm3' },
  water: { icon: Drop, color: '#3B82F6', label: 'Water', unit: 'L' }
};

export default function EnergyPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [readings, setReadings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState('overview');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'electricity', reading: '', cost: '', date: new Date().toISOString().slice(0, 10), unit: 'kWh', notes: '' });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [readRes, analRes] = await Promise.all([
        api.get('/energy/readings'),
        api.get('/energy/analytics')
      ]);
      setReadings(readRes.data);
      setAnalytics(analRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [api]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/energy/readings', { ...form, reading: Number(form.reading), cost: Number(form.cost) });
      setShowForm(false);
      setForm({ type: 'electricity', reading: '', cost: '', date: new Date().toISOString().slice(0, 10), unit: 'kWh', notes: '' });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/energy/readings/${id}`);
      fetchData();
    } catch (err) { console.error(err); }
  };

  const cs = analytics?.currency_symbol || 'Rs.';

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const chartData = analytics?.monthly_trends?.reduce((acc, item) => {
    let existing = acc.find(a => a.month === item.month);
    if (!existing) { existing = { month: item.month }; acc.push(existing); }
    existing[item.type] = item.cost;
    return acc;
  }, []) || [];

  return (
    <div data-testid="energy-page" className="space-y-6 relative">
      <div className="glow-orb-orange -top-20 right-0 z-0" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 relative z-10">
        <div>
          <p className="overline">Utilities</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">Energy Monitor</h1>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="add-reading-btn"
          className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow flex items-center gap-2 w-fit">
          <Plus size={16} /> Add Reading
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-10">
        {Object.entries(TYPE_CONFIG).map(([key, config]) => {
          const typeData = analytics?.by_type?.[key];
          return (
            <div key={key} className="glass-card p-6 animate-fade-up" data-testid={`energy-card-${key}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: `${config.color}15` }}>
                  <config.icon size={20} weight="duotone" style={{ color: config.color }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{config.label}</p>
                  <p className="text-[11px] text-neutral-500">{typeData?.count || 0} readings</p>
                </div>
              </div>
              <p className="text-2xl font-heading font-bold text-white">{cs}{(typeData?.total_cost || 0).toLocaleString('en-IN')}</p>
              <p className="text-xs text-neutral-500 mt-1">{(typeData?.total_reading || 0).toLocaleString()} {config.unit} total</p>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/5 rounded-xl p-1 w-fit relative z-10">
        {['overview', 'readings'].map(tabName => (
          <button key={tabName} onClick={() => setTab(tabName)} data-testid={`energy-tab-${tabName}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${tab === tabName ? 'bg-[#141414] shadow-sm text-white' : 'text-neutral-500 hover:text-white'}`}>
            {tabName === 'overview' ? 'Analytics' : 'Readings'}
          </button>
        ))}
      </div>

      {/* Chart */}
      {tab === 'overview' && (
        <div className="glass-card p-6 relative z-10" data-testid="energy-chart">
          <p className="overline mb-4">Monthly Cost Trend</p>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: '#A3A3A3', fontSize: 11 }} axisLine={false} />
                <YAxis tick={{ fill: '#A3A3A3', fontSize: 11 }} axisLine={false} />
                <Tooltip contentStyle={{ background: 'rgba(20,20,20,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12 }} />
                <Bar dataKey="electricity" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="gas" fill="#FF6B35" radius={[4, 4, 0, 0]} />
                <Bar dataKey="water" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-neutral-500 text-sm text-center py-12">Add readings to see analytics</p>
          )}
        </div>
      )}

      {/* Readings List */}
      {tab === 'readings' && (
        <div className="space-y-2 relative z-10" data-testid="energy-readings-list">
          {readings.length === 0 ? (
            <p className="text-neutral-500 text-sm text-center py-12">No readings yet</p>
          ) : readings.map(r => {
            const config = TYPE_CONFIG[r.type] || TYPE_CONFIG.electricity;
            return (
              <div key={r.reading_id} className="glass-card px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <config.icon size={20} style={{ color: config.color }} weight="duotone" />
                  <div>
                    <p className="text-sm text-white font-medium">{config.label} — {r.reading} {r.unit}</p>
                    <p className="text-[11px] text-neutral-500">{r.date}{r.notes ? ` | ${r.notes}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-semibold text-white">{cs}{r.cost?.toLocaleString('en-IN')}</span>
                  <button onClick={() => handleDelete(r.reading_id)} className="text-neutral-600 hover:text-red-400 transition-colors">
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">Add Energy Reading</h3>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className="overline block mb-2">Type</label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value, unit: TYPE_CONFIG[e.target.value]?.unit || '' })}
                  data-testid="energy-type-select"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50">
                  <option value="electricity">Electricity</option>
                  <option value="gas">Gas</option>
                  <option value="water">Water</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">Reading ({form.unit})</label>
                  <input type="number" value={form.reading} onChange={e => setForm({ ...form, reading: e.target.value })} required
                    data-testid="energy-reading-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
                <div>
                  <label className="overline block mb-2">Cost ({cs})</label>
                  <input type="number" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} required
                    data-testid="energy-cost-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Date</label>
                <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                  data-testid="energy-date-input"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">Notes (optional)</label>
                <input type="text" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" placeholder="e.g., AC heavy use" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">Cancel</button>
                <button type="submit" data-testid="save-reading-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">Save Reading</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
